import { X, Sparkles, Trash2, Loader, FolderOpen } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useAuth0UserId } from '../hooks/useAuth0UserId';
import * as promptApi from '../services/promptApi';

interface SavedPrompt {
  id: string;
  name: string;
  prompt: string;
  timestamp: string;
}

interface GenerateFlowPanelProps {
  onClose: () => void;
  onGenerate: (description: string) => Promise<void>;
  initialDescription?: string;
}

export default function GenerateFlowPanel({ onClose, onGenerate, initialDescription = '' }: GenerateFlowPanelProps) {
  const [description, setDescription] = useState(initialDescription);
  const [savedPrompts, setSavedPrompts] = useState<SavedPrompt[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [sortBy, setSortBy] = useState<'date' | 'name'>('date');
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(false);
  const userId = useAuth0UserId();

  useEffect(() => {
    loadSavedPrompts();
    if (initialDescription) {
      setDescription(initialDescription);
    }
  }, [initialDescription, userId]);

  const loadSavedPrompts = async () => {
    if (!userId) {
      // Fallback a localStorage se non autenticato
      const saved = localStorage.getItem('pigna-flow-user-prompts');
      if (saved) {
        try {
          const prompts = JSON.parse(saved);
          setSavedPrompts(prompts);
        } catch (e) {
          console.error('Errore caricamento prompt:', e);
          setSavedPrompts([]);
        }
      }
      return;
    }

    setIsLoadingPrompts(true);
    try {
      const result = await promptApi.listPrompts(userId, {
        node_type: 'Generate',
        limit: 100
      });
      
      // Converti da Prompt API a SavedPrompt
      const converted = result.prompts.map(p => ({
        id: p.id,
        name: p.name,
        prompt: p.content,
        timestamp: p.created_at
      }));
      
      setSavedPrompts(converted);
    } catch (error) {
      console.error('Errore caricamento prompt da API:', error);
      // Fallback a localStorage
      const saved = localStorage.getItem('pigna-flow-user-prompts');
      if (saved) {
        try {
          const prompts = JSON.parse(saved);
          setSavedPrompts(prompts);
        } catch (e) {
          setSavedPrompts([]);
        }
      }
    } finally {
      setIsLoadingPrompts(false);
    }
  };

  const savePrompt = async () => {
    if (!description.trim()) {
      alert('❌ Scrivi prima una descrizione!');
      return;
    }
    const name = prompt('💾 Nome per questo prompt:', description.substring(0, 50) + (description.length > 50 ? '...' : ''));
    if (!name) return;
    
    if (!userId) {
      // Fallback a localStorage se non autenticato
      const newPrompt: SavedPrompt = {
        id: `prompt-${Date.now()}`,
        name: name.trim(),
        prompt: description.trim(),
        timestamp: new Date().toISOString()
      };
      
      const updated = [...savedPrompts, newPrompt];
      setSavedPrompts(updated);
      localStorage.setItem('pigna-flow-user-prompts', JSON.stringify(updated));
      alert(`✅ Prompt "${name}" salvato!`);
      return;
    }

    try {
      const created = await promptApi.createPrompt(
        {
          node_type: 'Generate',
          name: name.trim(),
          content: description.trim(),
        },
        userId
      );
      
      const newPrompt: SavedPrompt = {
        id: created.id,
        name: created.name,
        prompt: created.content,
        timestamp: created.created_at
      };
      
      setSavedPrompts([...savedPrompts, newPrompt]);
      alert(`✅ Prompt "${name}" salvato nel database!`);
    } catch (error: any) {
      console.error('Errore salvataggio prompt:', error);
      alert(`❌ Errore salvataggio: ${error.message}`);
    }
  };

  const deletePrompt = async (id: string, name: string) => {
    if (!confirm(`⚠️ Eliminare il prompt "${name}"?`)) return;
    
    if (!userId) {
      // Fallback a localStorage se non autenticato
      const updated = savedPrompts.filter(p => p.id !== id);
      setSavedPrompts(updated);
      localStorage.setItem('pigna-flow-user-prompts', JSON.stringify(updated));
      alert(`✅ Prompt "${name}" eliminato!`);
      return;
    }

    try {
      await promptApi.deletePrompt(id, userId);
      setSavedPrompts(savedPrompts.filter(p => p.id !== id));
      alert(`✅ Prompt "${name}" eliminato!`);
    } catch (error: any) {
      console.error('Errore eliminazione prompt:', error);
      alert(`❌ Errore eliminazione: ${error.message}`);
    }
  };

  const loadPrompt = (promptText: string) => {
    setDescription(promptText);
  };

  const handleGenerate = async () => {
    if (!description.trim()) {
      alert('❌ Inserisci una descrizione!');
      return;
    }
    setIsGenerating(true);
    try {
      await onGenerate(description.trim());
      onClose();
    } catch (error: any) {
      alert(`❌ Errore: ${error.message || error}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const sortedPrompts = [...savedPrompts].sort((a, b) => {
    if (sortBy === 'date') {
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    } else {
      return a.name.localeCompare(b.name);
    }
  });

  return (
    <div className="w-[600px] bg-white border-l-4 border-purple-300 flex flex-col overflow-hidden shadow-2xl h-full">
      {/* Header */}
      <div className="p-6 border-b-2 border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-purple-500" />
            Genera Flow con AI
          </h2>
          <button
            onClick={onClose}
            className="bg-gray-500 hover:bg-gray-600 text-white p-2 rounded-lg transition"
            title="Chiudi pannello"
          >
            <X size={20} />
          </button>
        </div>
        <p className="text-sm text-gray-600">
          Descrivi il flow in linguaggio naturale. L'AI lo trasformerà in un diagramma funzionante!
        </p>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {/* Textarea descrizione */}
        <div>
          <label className="block text-sm font-bold text-gray-900 mb-2">
            📝 Descrizione Flow
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Es: Saluta l'utente, chiedi il suo nome e città. Se la città è Roma, di' che è stupenda, altrimenti di' che è bella."
            className="w-full border-2 border-purple-200 rounded-lg px-4 py-3 text-sm min-h-[200px] focus:border-purple-400 focus:ring-2 focus:ring-purple-200 transition"
          />
          <div className="mt-2 flex items-center gap-2">
            <button
              onClick={savePrompt}
              disabled={!description.trim()}
              className="px-4 py-2 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white text-sm font-semibold rounded-lg transition"
            >
              💾 Salva Prompt
            </button>
            <button
              onClick={handleGenerate}
              disabled={!description.trim() || isGenerating}
              className={`flex-1 px-4 py-2 ${isGenerating ? 'bg-purple-300' : 'bg-purple-600 hover:bg-purple-700'} disabled:bg-gray-300 text-white text-sm font-bold rounded-lg transition flex items-center justify-center gap-2`}
            >
              {isGenerating ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Generazione in corso...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Genera Flow
                </>
              )}
            </button>
          </div>
        </div>

        {/* Libreria Prompt Salvati */}
        {isLoadingPrompts && (
          <div className="border-t-2 border-gray-200 pt-4">
            <div className="flex items-center justify-center py-4">
              <Loader className="w-5 h-5 animate-spin text-purple-500" />
              <span className="ml-2 text-sm text-gray-600">Caricamento prompt...</span>
            </div>
          </div>
        )}
        {!isLoadingPrompts && savedPrompts.length > 0 && (
          <div className="border-t-2 border-gray-200 pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                <FolderOpen className="w-4 h-4" />
                Prompt Salvati ({savedPrompts.length})
              </h3>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'date' | 'name')}
                className="px-2 py-1 text-xs border rounded"
              >
                <option value="date">📅 Per data</option>
                <option value="name">🔤 Per nome</option>
              </select>
            </div>
            <div className="space-y-2">
              {sortedPrompts.map(p => (
                <div
                  key={p.id}
                  className="bg-purple-50 rounded-lg p-3 border-2 border-purple-200 hover:border-purple-400 transition cursor-pointer group"
                  onClick={() => loadPrompt(p.prompt)}
                >
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex-1">
                      <div className="text-sm font-bold text-purple-900">{p.name}</div>
                      <div className="text-xs text-gray-500">{new Date(p.timestamp).toLocaleString('it-IT')}</div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deletePrompt(p.id, p.name);
                      }}
                      className="p-1.5 bg-red-500 hover:bg-red-600 text-white rounded transition opacity-0 group-hover:opacity-100"
                      title="Elimina prompt"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="text-xs text-gray-700 line-clamp-2">
                    {p.prompt}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

